import { FilterNode } from '@blisspointmedia/bpm-types/dist/querying';
import { DimensionId } from '@blisspointmedia/bpm-types/dist/querying/ids';
import {
  CARD_SIZES,
  CardSize,
  ChartConfigInput,
  ChartId,
  ChartType,
  coerceCardSize,
  defaultCardSizeForChartType
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { AgentResponse, AgentToolCallSegment } from '../../utils/agent-client';
import { isPromotionIntent, PromotionIntentType } from '../Charts/hooks/usePendingPromotion';
import {
  isSharingAudience,
  isSharingRequestTarget,
  SharingRequest
} from '../Charts/chart-permissions';
import { DashboardCard, LayoutChange } from '../Dashboard/dashboard-types';
import { isFilterNodeList, isIgnoreList } from '../Dashboard/dashboard-filter-utils';
import { SizedChart } from '../Dashboard/hooks/useDashboard';

/**
 * A dashboard's cards in the order their `move_card` SLOT numbers are assigned.
 *
 * That is plain ARRAY order, and this exists to say so and to keep a test on it. The slot number the
 * agent is shown has to mean the same thing as the slot `moveCardToSlot` acts on, and that function
 * counts through the array (see its doc comment -- array order is the persisted record of what the
 * user built, and it must not be re-derived from grid position).
 *
 * This USED TO `.sort(byGridOrder)`, back when the two always agreed: without stacking, packing
 * left-to-right means array index and reading order rise together. Vertical stacking broke that.
 * Four `one-third`s pack to a@0,0 b@0,5 c@4,0 d@4,5, so reading order is a, c, b, d while the array is
 * a, b, c, d -- the agent was told c was slot 2 while the packer's slot 2 was b, so "move d to slot
 * 2" reordered the wrong card, and asking for a card's own slot silently did nothing while the
 * agent reported success. Do not re-add a positional sort here.
 */
export const cardsInSlotOrder = (cards: DashboardCard[]): DashboardCard[] => cards;

/**
 * Decide the card size for one chart of an agent-emitted dashboard plan.
 *
 * bpm-types' `CARD_SIZING_BY_CHART_TYPE` is the single source of truth for which sizes a chart type
 * may take, and this is the ONLY place the whole-dashboard build consults it -- the menu resize
 * (`cardSizesForChartType`) and the agent's `set_card_size` (`resizeCardInList`) each have their own
 * funnel. It used to be a hand-rolled `type === 'table' ? 'wide' : DEFAULT_CARD_SIZE` ternary, which
 * both duplicated the rule and contradicted it: a `stat` allows only `one-third`, so every stat the
 * agent planned was persisted at `standard`, a size its own entry forbids.
 *
 * `size` is a HINT the agent may omit (an older agent always does) and may get wrong, so it is
 * coerced rather than trusted. `type` is carried onto the returned chart because `fillRowGaps` skips
 * its matrix check entirely when the type is absent -- omitting it is what let gap filling widen a
 * stat card past what it allows.
 */
export const sizedChartForPlan = (
  chartId: ChartId,
  type: ChartType,
  size: CardSize | undefined,
  ignoreDashboardFilters?: DimensionId[]
): SizedChart => ({
  chartId,
  size: size ? coerceCardSize(type, size) : defaultCardSizeForChartType(type),
  type,
  ...(ignoreDashboardFilters ? { ignoreDashboardFilters } : {})
});

const tryParse = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// An emitted config may legitimately have no `id` — a brand-new chart leaves it to the database.
const looksLikeChartConfig = (val: unknown): val is ChartConfigInput =>
  typeof val === 'object' && val !== null && Array.isArray((val as { queries?: unknown }).queries);

// Scan for top-level balanced `{ }` objects, ignoring braces inside strings.
const scanJsonObjects = (text: string): string[] => {
  const objects: string[] = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
    } else if (ch === '"') {
      inString = true;
    } else if (ch === '{') {
      if (depth === 0) {
        start = i;
      }
      depth += 1;
    } else if (ch === '}' && depth > 0) {
      depth -= 1;
      if (depth === 0 && start !== -1) {
        objects.push(text.slice(start, i + 1));
        start = -1;
      }
    }
  }
  return objects;
};

// The dashboard agent embeds the ChartConfig as JSON in its reply text — inside a
// ```json fenced block or inline. Pull the first JSON object that looks like a
// ChartConfig (has a `queries` array); the backend does the authoritative validation.
export const extractChartConfig = (text: string): ChartConfigInput | null => {
  const fenced = [...text.matchAll(/```(?:json)?\s*([\s\S]*?)```/gi)].map(m => m[1]);
  const candidates = [...fenced, ...scanJsonObjects(text)];
  for (const candidate of candidates) {
    try {
      const parsed: unknown = JSON.parse(candidate.trim());
      if (looksLikeChartConfig(parsed)) return parsed;
    } catch {
      // not valid JSON — try the next candidate
    }
  }
  return null;
};

// The agent emits its final ChartConfigs through `emit_chart_config` tool calls (its structured
// output), not as JSON in its prose. A single turn can emit MULTIPLE — one per chart — e.g. when
// the user says "update these 4 charts", so collect EVERY emitted config in emit order. (Reading
// only the last one silently dropped all but one — "update these 4" saved just the last chart.)
//
// Dedupe by the call's `slot`, last-wins and position-preserving: within a turn the agent may
// re-emit the same chart after fixing a validation error, and we want the corrected final version.
// This USED to key off `config.id`, which no longer works — a brand-new chart carries no id (the
// database assigns it), so every new chart in a turn would collide on `undefined`. `slot` is the
// agent's own within-turn label for "which chart this call is about"; see the agent's core.py.
//
// A call with no usable slot is never deduped — it is appended. That is the safe direction: an
// extra chart is visible and removable, a dropped one is silent.
export const extractAllEmittedConfigs = (response: AgentResponse): ChartConfigInput[] => {
  const configs: ChartConfigInput[] = [];
  const indexBySlot = new Map<string, number>();
  for (const s of response.segments) {
    if (s.type !== 'tool_call' || s.name !== 'emit_chart_config') continue;
    const raw = s.args.config_json ?? s.args.config;
    const parsed: unknown = typeof raw === 'string' ? tryParse(raw) : raw;
    if (!looksLikeChartConfig(parsed)) continue;
    const slot = typeof s.args.slot === 'string' && s.args.slot ? s.args.slot : null;
    const at = slot === null ? undefined : indexBySlot.get(slot);
    if (at === undefined) {
      if (slot !== null) {
        indexBySlot.set(slot, configs.length);
      }
      configs.push(parsed);
    } else {
      configs[at] = parsed;
    }
  }
  if (configs.length > 0) return configs;
  // Fallback: an agent that inlined the JSON in prose (single chart only).
  const fromText = extractChartConfig(response.text);
  return fromText ? [fromText] : [];
};

// The user's chat-driven decision about the current preview: the agent emits a `promote_chart` tool
// call carrying an `intent` (update / save_as_new / discard). Read the LAST such call's intent so a
// turn that both edits and keeps ("make it a bar chart and save it") promotes after the edit lands.
// Returns null when absent or the intent isn't one of the known verbs (the agent excludes "undo" and
// asks instead, so no tool call — nothing to promote).
export const extractPromotion = (response: AgentResponse): PromotionIntentType | null => {
  const promote = [...response.segments]
    .reverse()
    .find((s): s is AgentToolCallSegment => s.type === 'tool_call' && s.name === 'promote_chart');
  const intent = promote?.args.intent;
  return isPromotionIntent(intent) ? intent : null;
};

// A `size` the agent invented fails whatever carries it, same as any other malformed field: a plan
// falls back to single-chart handling and a resize is dropped, rather than laying the dashboard out
// wrong.
const isCardSize = (val: unknown): val is CardSize => CARD_SIZES.some(s => s === val);

// Every dashboard change the turn asked for (set_card_size / remove_card / move_card /
// set_card_dashboard_filter_optout / set_dashboard_filters), in call order —
// NOT last-one-wins like promotion, since one turn can legitimately make several ("make the tables
// full width" is one call per table), and order matters when a move follows another.
//
// Args come off the tool CALL, so they carry the tool's own parameter names (`chart_id`), not the
// camelCase the result echoes. Every field is re-checked here even though the tools validate them:
// this is the side that applies the change, and a malformed call is dropped rather than trusted.
const FILTER_TOOLS = new Set(['set_dashboard_filters', 'set_card_dashboard_filter_optout']);

export const extractLayoutChanges = (response: AgentResponse): LayoutChange[] =>
  response.segments
    .filter((s): s is AgentToolCallSegment => s.type === 'tool_call')
    .flatMap((s): LayoutChange[] => {
      const { chart_id: rawChartId, size, position, ignore, filters_json: rawFilters } = s.args;
      // The filter tools check more strictly than this parser, so drop a call they rejected.
      if (FILTER_TOOLS.has(s.name) && s.resultRejected) return [];
      if (s.name === 'set_dashboard_filters') {
        const filters: unknown = typeof rawFilters === 'string' ? tryParse(rawFilters) : rawFilters;
        return isFilterNodeList(filters) ? [{ kind: 'setDashboardFilters', filters }] : [];
      }
      if (typeof rawChartId !== 'string' || !rawChartId) return [];
      const chartId = rawChartId as ChartId;
      switch (s.name) {
        case 'set_card_size':
          return isCardSize(size) ? [{ kind: 'resize', chartId, size }] : [];
        case 'remove_card':
          return [{ kind: 'remove', chartId }];
        case 'move_card':
          return typeof position === 'number' && Number.isInteger(position) && position >= 1
            ? [{ kind: 'move', chartId, slot: position }]
            : [];
        case 'set_card_dashboard_filter_optout':
          return isIgnoreList(ignore) ? [{ kind: 'setCardFilterOptOut', chartId, ignore }] : [];
        default:
          return [];
      }
    });

// A whole-dashboard plan the agent emits via the `emit_dashboard_config` tool call: a named
// dashboard plus an ORDERED list of chart configs. The plan carries no POSITIONS — the frontend
// assigns all placement — but a chart may carry a preset `size` as a hint. `size` is optional in
// both directions: an older agent omits it and the frontend picks a default (see AgentDrawer), and
// an agent that sends one is tolerated by a frontend that ignores it. Local for now; promotable to
// bpm-types later.
export interface DashboardBuildPlan {
  name: string;
  defaultFilters?: FilterNode[];
  charts: { config: ChartConfigInput; size?: CardSize; ignoreDashboardFilters?: DimensionId[] }[];
}

const isRecord = (val: unknown): val is Record<string, unknown> =>
  typeof val === 'object' && val !== null;

// A bad or empty filter list or opt-out is dropped; the plan itself stays valid.
const sanitizeDashboardPlan = (val: unknown): DashboardBuildPlan | null => {
  if (!isRecord(val)) return null;
  const { name, charts, defaultFilters } = val;
  if (typeof name !== 'string' || name.trim() === '') return null;
  if (!Array.isArray(charts) || charts.length === 0) return null;
  const cleaned: DashboardBuildPlan['charts'] = [];
  for (const c of charts) {
    if (!isRecord(c)) return null;
    const { config, size, ignoreDashboardFilters } = c;
    if (!looksLikeChartConfig(config)) return null;
    if (size !== undefined && !isCardSize(size)) return null;
    cleaned.push({
      config,
      ...(size === undefined ? {} : { size }),
      ...(isIgnoreList(ignoreDashboardFilters) && ignoreDashboardFilters.length > 0
        ? { ignoreDashboardFilters }
        : {})
    });
  }
  return {
    name,
    charts: cleaned,
    ...(isFilterNodeList(defaultFilters) && defaultFilters.length > 0 ? { defaultFilters } : {})
  };
};

// The agent emits a full dashboard plan through the `emit_dashboard_config` tool call. Read the last
// such call the tool did not reject (a rejected call can be the turn's last), parse its
// `config_json` or `config`, and validate the shape (non-empty name + non-empty charts, each with a
// chart-config-shaped `config`). Returns null when absent or malformed — the caller falls back to
// single-chart handling. Valid optional `defaultFilters` and per-chart opt-outs are kept; invalid
// ones are dropped.
export const extractEmittedDashboardConfig = (
  response: AgentResponse
): DashboardBuildPlan | null => {
  const emit = [...response.segments]
    .reverse()
    .find(
      (s): s is AgentToolCallSegment =>
        s.type === 'tool_call' && s.name === 'emit_dashboard_config' && !s.resultRejected
    );
  const raw = emit?.args.config_json ?? emit?.args.config;
  const parsed: unknown = typeof raw === 'string' ? tryParse(raw) : raw;
  return sanitizeDashboardPlan(parsed);
};

// The user's chat-driven request to change who can see or who can edit the focused record: the agent
// emits a `set_sharing` tool call naming a `target` and one or both AUDIENCES (never a level).
//
// EVERY FIELD IS RE-VALIDATED HERE. `agent-client.ts` builds each
// segment from the ARGUMENTS the model passed, not from the tool's return value — so the tool
// rejecting a bad audience does nothing for us. The tool's rejection makes the MODEL retry inside
// the turn; reading the LAST matching call is what lands that retry. Same division of labour as
// `extractPromotion` and `isPromotionIntent`.
//
// REJECTS THE WHOLE REQUEST if any field is unrecognised, rather than keeping the half it
// understands. A request assembled from part of what the user said would re-share a record on terms
// nobody asked for, and there is no channel to tell the agent that happened.
export const extractSharingRequest = (response: AgentResponse): SharingRequest | null => {
  const call = [...response.segments]
    .reverse()
    .find((s): s is AgentToolCallSegment => s.type === 'tool_call' && s.name === 'set_sharing');
  if (!call) return null;

  const { target, visible_to: rawVisibleTo, editable_by: rawEditableBy } = call.args;
  if (!isSharingRequestTarget(target)) return null;
  // `null` and `undefined` both mean "leave this column alone". The tool's Python signature defaults
  // both audiences to None, so a model that passes an optional argument explicitly sends null — and
  // treating that as a bad VALUE would reject the whole request in silence.
  const visibleTo = rawVisibleTo ?? undefined;
  const editableBy = rawEditableBy ?? undefined;
  if (visibleTo !== undefined && !isSharingAudience(visibleTo)) return null;
  if (editableBy !== undefined && !isSharingAudience(editableBy)) return null;
  // Nothing to change is not a request.
  if (visibleTo === undefined && editableBy === undefined) return null;

  return {
    target,
    ...(visibleTo === undefined ? {} : { visibleTo }),
    ...(editableBy === undefined ? {} : { editableBy })
  };
};

// A random tip the viewer hasn't seen yet, or undefined once every tip has been shown. Random rather
// than cycling in list order, so the first tip someone meets differs from visit to visit.
export const pickUnseenTip = (
  tips: readonly string[],
  seen: readonly string[],
  random: () => number = Math.random
): string | undefined => {
  const unseen = tips.filter(tip => !seen.includes(tip));
  return unseen.length ? unseen[Math.floor(random() * unseen.length)] : undefined;
};
